import { t } from '@glory/i18n';
import { Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { animate, AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from 'react';

import { IconButton } from '../components/button.tsx';
import { Shortcut } from '../components/kbd.tsx';
import { Tooltip, TooltipProvider } from '../components/tooltip.tsx';
import { cn } from '../lib/cn.ts';
import { useMediaQuery } from '../lib/media.ts';
import { collapse, TRANSITION } from '../lib/motion.tsx';
import { hotkeyLabel, useHotkeys } from './hotkeys.ts';
import { useImmersiveState } from './immersive.ts';
import { useKeepGutterWhileLocked } from './lock-gutter.ts';
import { LogoBadge } from './logo.tsx';

export interface NavItem<P extends string = string> {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly icon: ReactNode;
  /** Последовательность клавиш перехода: 'g t'. */
  readonly hotkey?: string;
  /** Счётчик: непрочитанное, входящие. */
  readonly count?: number;
  /** Подпись справа от названия (версия приложения, режим). В свёрнутом меню не показывается. */
  readonly note?: string;
  /** Нижняя группа меню (настройки). */
  readonly bottom?: boolean;
  /** Группа пунктов («работа», «продажи», «студия»): между группами — промежуток, без подписей. */
  readonly group?: string;
  /** Показывать в нижней панели на телефоне. */
  readonly mobile?: boolean;
  /** Разрешение, без которого пункт не показывается (см. `allowedFor`); тип задаёт приложение. */
  readonly permission?: P;
}

export type LinkRenderer = (props: {
  href: string;
  className: string;
  children: ReactNode;
  'aria-current'?: 'page';
  onClick?: () => void;
}) => ReactNode;

const SIDEBAR_KEY = 'glory.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function Logo({ compact }: { compact: boolean }) {
  return (
    <span className="flex items-center gap-2 text-white">
      <LogoBadge className="inline-grid size-8 shrink-0 place-items-center rounded-md bg-white text-[#0D0D0D]" />
      {compact ? null : (
        <span className="animate-fade-in font-display text-[0.9375rem] font-bold tracking-[0.02em]">
          {t('app.logo')}
          <span className="text-accent">{t('app.logoDot')}</span>
          {t('app.logoSuffix')}
        </span>
      )}
    </span>
  );
}

export interface AppShellProps {
  nav: readonly NavItem[];
  activeId?: string | undefined;
  renderLink: LinkRenderer;
  /** Правая часть шапки: колокольчик, меню пользователя. */
  actions?: ReactNode;
  /** Открыть палитру ⌘K. */
  onOpenCommand: () => void;
  /** Переход по горячей клавише пункта меню (`G …`). */
  onNavigate: (href: string) => void;
  /** Строка над содержимым: хлебные крошки. */
  breadcrumbs?: ReactNode;
  /** Спрятать нижнюю панель телефона: открытый чат занимает весь экран, как в Telegram. */
  hideMobileNav?: boolean;
  /**
   * Свернуть меню до значков, что бы ни выбрал пользователь: трёхзонным разделам («Сообщения», «Почта») на
   * ноутбуке нужна ширина. Кнопка внизу меню разворачивает его до ухода из раздела; выбор пользователя не меняется.
   */
  compact?: boolean;
  children: ReactNode;
}

/** Оболочка приложения: чёрное меню слева, шапка с поиском, содержимое; на телефоне — нижняя панель. */
export function AppShell({
  nav,
  activeId,
  renderLink,
  actions,
  onOpenCommand,
  onNavigate,
  breadcrumbs,
  hideMobileNav = false,
  compact: forceCompact = false,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Развернули меню в разделе, где оно свёрнуто само: до ухода из раздела.
  const [expandedHere, setExpandedHere] = useState(false);
  const [wasForced, setWasForced] = useState(forceCompact);
  if (wasForced !== forceCompact) {
    setWasForced(forceCompact);
    setExpandedHere(false);
  }
  const narrow = forceCompact ? !expandedHere : collapsed;
  const [mobileMore, setMobileMore] = useState(false);
  useKeepGutterWhileLocked();
  // Плашка активного пункта переезжает к новому пункту; у меню и листа «Ещё» — свои.
  const pill = useId();

  // Смена раздела — мягкое проявление содержимого. Без перемонтирования и без задержки:
  // страница рисуется сразу, анимация начинается до первой отрисовки.
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!mainRef.current) return;
    const appear = animate(mainRef.current, { opacity: [0, 1] }, TRANSITION.enter);
    return () => {
      appear.complete();
    };
  }, [activeId]);

  useHotkeys([
    { keys: 'mod+k', handler: onOpenCommand },
    ...nav.flatMap((n) =>
      n.hotkey
        ? [
            {
              keys: n.hotkey,
              handler: () => {
                onNavigate(n.href);
              },
            },
          ]
        : [],
    ),
  ]);

  const toggle = () => {
    if (forceCompact) {
      setExpandedHere((value) => !value);
      return;
    }
    setCollapsed((value) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, value ? '0' : '1');
      } catch {
        // Хранилище недоступно — состояние меню живёт до перезагрузки.
      }
      return !value;
    });
  };

  const item = (entry: NavItem, compact: boolean, group: string, onClick?: () => void, gap = false) => {
    const active = entry.id === activeId;
    const link = renderLink({
      href: entry.href,
      'aria-current': active ? 'page' : undefined,
      ...(onClick ? { onClick } : {}),
      className: cn(
        'group relative flex h-9 items-center gap-2.5 rounded-md px-2.5 font-semibold text-sidebar-text transition-colors',
        'hover:bg-sidebar-item hover:text-white focus-visible:outline-accent [&_svg]:size-[1.125rem] [&_svg]:shrink-0',
        active && 'font-bold text-on-accent hover:bg-transparent hover:text-on-accent',
        compact && 'justify-center px-0',
      ),
      children: (
        <>
          {active ? (
            <motion.span
              aria-hidden="true"
              layoutId={`${pill}-${group}`}
              transition={TRANSITION.layout}
              className="absolute inset-0 z-0 rounded-md bg-accent"
            />
          ) : null}
          {/* Значок и подписи — над плашкой, в том числе над пролетающей мимо (меню — isolate). */}
          <span className="relative z-10 flex">
            {entry.icon}
            {/* Свёрнутое меню: подписи нет, счётчик — точкой на значке. */}
            {compact && entry.count ? (
              <span
                key={entry.count}
                className="absolute -top-1.5 left-[calc(100%-0.375rem)] min-w-4 animate-pop-in rounded-full bg-accent px-1 text-center text-[0.625rem] leading-4 font-extrabold text-on-accent ring-2 ring-sidebar"
              >
                {entry.count > 99 ? '99+' : entry.count}
              </span>
            ) : null}
          </span>
          {compact ? null : (
            <span className="relative z-10 flex-1 animate-fade-in truncate">{entry.label}</span>
          )}
          {!compact && entry.note ? (
            <span className="relative z-10 shrink-0 text-caption font-semibold text-sidebar-text">
              {entry.note}
            </span>
          ) : null}
          {!compact && entry.count ? (
            <span
              key={entry.count}
              className={cn(
                'relative z-10 animate-pop-in text-caption font-bold',
                active ? 'text-on-accent' : 'text-white',
              )}
            >
              {entry.count}
            </span>
          ) : null}
        </>
      ),
    });
    return (
      <li key={entry.id} className={gap ? 'mt-3' : undefined}>
        {compact ? (
          <Tooltip
            side="right"
            content={entry.hotkey ? `${entry.label} · ${hotkeyLabel(entry.hotkey).join(' ')}` : entry.label}
          >
            <span className="block">{link}</span>
          </Tooltip>
        ) : (
          link
        )}
      </li>
    );
  };

  const main = nav.filter((n) => !n.bottom);
  const bottom = nav.filter((n) => n.bottom);
  const mobile = nav.filter((n) => n.mobile);
  const mobileRest = nav.filter((n) => !n.mobile);

  const immersive = useImmersiveState();
  const phone = useMediaQuery('(max-width: 767px)');
  // Полноэкранный слой (звонок, чат и карточка на телефоне): шапку и меню не рисуем вовсе.
  const bare = immersive.all || (phone && immersive.mobile);
  const navShown = !bare && !hideMobileNav;
  useLayoutEffect(() => {
    // Высота нижнего меню для расчётов (`--nav-h`): скрытое меню места не занимает.
    document.documentElement.toggleAttribute('data-nav-hidden', hideMobileNav);
    return () => {
      document.documentElement.removeAttribute('data-nav-hidden');
    };
  }, [hideMobileNav]);

  return (
    <TooltipProvider>
      <div className="flex min-h-dvh bg-bg pt-[var(--call-bar-h)] text-text">
        {/* Место под меню: само меню закреплено (`fixed`) и не уезжает при прокрутке длинных страниц. */}
        {immersive.all ? null : (
          <div
            aria-hidden="true"
            className={cn(
              'hidden shrink-0 transition-[width] duration-200 ease-out-quick md:block',
              narrow ? 'w-16' : 'w-58',
            )}
          />
        )}
        {immersive.all ? null : (
          <aside
            aria-label={t('shell.mainMenu')}
            className={cn(
              'fixed top-[var(--call-bar-h)] bottom-0 left-0 z-[var(--z-header)] hidden flex-col overflow-hidden bg-sidebar px-2.5 pt-4 pb-3 md:flex',
              'transition-[width] duration-200 ease-out-quick',
              narrow ? 'w-16' : 'w-58',
            )}
          >
            <div className={cn('mb-5 flex items-center px-2', narrow && 'justify-center px-0')}>
              <Logo compact={narrow} />
            </div>
            <nav className="isolate flex flex-1 flex-col">
              <ul className="flex flex-col gap-0.5">
                {main.map((n, index) =>
                  item(n, narrow, 'side', undefined, index > 0 && main[index - 1]?.group !== n.group),
                )}
              </ul>
              <ul className="mt-auto flex flex-col gap-0.5">{bottom.map((n) => item(n, narrow, 'side'))}</ul>
            </nav>
            <button
              type="button"
              onClick={toggle}
              className={cn(
                'mt-2 flex h-8 items-center gap-2 rounded-md px-2.5 text-small font-semibold text-sidebar-text hover:bg-sidebar-item hover:text-white',
                'focus-visible:outline-accent [&_svg]:size-4',
                narrow && 'justify-center px-0',
              )}
              aria-label={narrow ? t('shell.expandMenu') : t('shell.collapseMenu')}
            >
              {narrow ? <PanelLeftOpen /> : <PanelLeftClose />}
              {narrow ? null : <span className="animate-fade-in truncate">{t('shell.collapseMenu')}</span>}
            </button>
          </aside>
        )}

        <div className="flex min-w-0 flex-1 flex-col pb-[var(--nav-h)] md:pb-0">
          {bare ? null : (
            <header
              data-app-header=""
              // Размытие фона — только на компьютере: на телефонах `backdrop-blur` у sticky-шапки рисовался поверх
              // fixed-слоёв (баг композитинга мобильных браузеров).
              className="sticky top-[var(--call-bar-h)] z-[var(--z-header)] flex h-[var(--header-h)] items-center gap-3 border-b border-border-soft bg-bg px-4 pt-[var(--safe-top)] md:bg-bg/90 md:px-6 md:pt-0 md:backdrop-blur"
            >
              <div className="md:hidden">
                <LogoBadge className="inline-grid size-10 shrink-0 place-items-center rounded-md bg-white text-[#0D0D0D] ring-1 ring-black/10" />
              </div>
              <div className="min-w-0 flex-1 truncate text-small font-bold text-muted">
                {breadcrumbs ?? nav.find((n) => n.id === activeId)?.label}
              </div>
              <button
                type="button"
                onClick={onOpenCommand}
                className={cn(
                  'hidden h-8 w-64 items-center gap-2 rounded-md border-thick border-border bg-surface px-2.5 text-muted sm:flex',
                  'font-medium hover:bg-surface-2 [&_svg]:size-4',
                )}
              >
                <Search />
                <span className="flex-1 text-left">{t('shell.search')}</span>
                <Shortcut keys={hotkeyLabel('mod+k')} />
              </button>
              <IconButton
                className="sm:hidden"
                label={t('shell.search')}
                icon={<Search />}
                onClick={onOpenCommand}
              />
              {actions}
            </header>
          )}
          <main ref={mainRef} className="min-w-0 flex-1">
            {children}
          </main>
        </div>

        {navShown ? (
          <nav
            aria-label={t('shell.mainMenu')}
            data-mobile-nav=""
            className="fixed inset-x-0 bottom-0 z-[var(--z-nav)] border-t border-sidebar-item bg-sidebar pb-[env(safe-area-inset-bottom)] md:hidden"
          >
            <AnimatePresence initial={false}>
              {mobileMore ? (
                <motion.div key="more" {...collapse}>
                  <ul className="isolate grid grid-cols-2 gap-1 border-b border-sidebar-item p-2">
                    {mobileRest.map((n) =>
                      item(n, false, 'sheet', () => {
                        setMobileMore(false);
                      }),
                    )}
                  </ul>
                </motion.div>
              ) : null}
            </AnimatePresence>
            <ul className="grid auto-cols-fr grid-flow-col">
              {mobile.map((n) => {
                const active = n.id === activeId;
                return (
                  <li key={n.id}>
                    {renderLink({
                      href: n.href,
                      'aria-current': active ? 'page' : undefined,
                      className: cn(
                        'flex h-14 flex-col items-center justify-center gap-0.5 text-caption font-semibold text-sidebar-text',
                        'transition-colors [&_svg]:size-5 [&_svg]:transition-transform active:[&_svg]:scale-90',
                        active && 'text-accent',
                      ),
                      children: (
                        <>
                          <span className="relative flex">
                            {n.icon}
                            {n.count ? (
                              <span
                                key={n.count}
                                className="absolute -top-1.5 left-[calc(100%-0.375rem)] min-w-4 animate-pop-in rounded-full bg-accent px-1 text-center text-[0.625rem] leading-4 font-extrabold text-on-accent"
                              >
                                {n.count > 99 ? '99+' : n.count}
                              </span>
                            ) : null}
                          </span>
                          <span>{n.label}</span>
                        </>
                      ),
                    })}
                  </li>
                );
              })}
              <li>
                <button
                  type="button"
                  aria-expanded={mobileMore}
                  onClick={() => {
                    setMobileMore((v) => !v);
                  }}
                  className={cn(
                    'flex h-14 w-full flex-col items-center justify-center gap-0.5 text-caption font-semibold text-sidebar-text',
                    'transition-colors [&_svg]:size-5 [&_svg]:transition-transform active:[&_svg]:scale-90',
                    mobileMore && 'text-white',
                  )}
                >
                  <Menu />
                  <span>{t('shell.more')}</span>
                </button>
              </li>
            </ul>
          </nav>
        ) : null}
      </div>
    </TooltipProvider>
  );
}

/** Заголовок страницы: крошки, название, действия справа. */
export function PageHeader({
  title,
  crumb,
  actions,
  className,
}: {
  title: ReactNode;
  crumb?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        {crumb ? <div className="mb-1 text-small font-bold text-muted">{crumb}</div> : null}
        <h1 className="truncate font-display text-heading font-bold">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}
